import React, { useState } from 'react';
import templateStrategyService from '@/services/templateStrategyService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TemplateStrategyDebug: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: 1,
    template_id: 1,
    strategy_type: 'reminder',
    tag_code: 'test',
    days_before_due: 3,
    days_after_due: 0,
    is_active: true,
    send_time: '09:00',
    max_attempts: 3,
    priority: 1,
    description: 'Teste de debug',
    business_rule: 'Teste'
  });

  const addLog = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const clearLogs = () => {
    setTestResults([]);
  };

  const testApiConfiguration = async () => {
    setIsLoading(true);
    addLog('🔍 Testando configuração da API...');
    
    try {
      // Debug da configuração
      templateStrategyService.debugApiConfiguration();
      addLog('✅ Debug da configuração executado (ver console)');
      
      // Teste de conectividade
      const isConnected = await templateStrategyService.testConnection();
      if (isConnected) {
        addLog('✅ Conectividade da API testada com sucesso');
      } else {
        addLog('❌ Falha na conectividade da API');
      }
    } catch (error) {
      addLog(`❌ Erro no teste de configuração: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const testCreateStrategy = async () => {
    setIsLoading(true);
    addLog('🚀 Testando criação de estratégia...');
    addLog(`📦 Dados: ${JSON.stringify(formData, null, 2)}`);
    
    try {
      const result = await templateStrategyService.createStrategy(formData);
      addLog(`✅ Estratégia criada com sucesso: ${JSON.stringify(result, null, 2)}`);
    } catch (error: any) {
      addLog(`❌ Erro ao criar estratégia: ${error.message}`);
      if (error.message.includes('404')) {
        addLog('🔍 Erro 404 indica que a rota não foi encontrada');
        addLog('🔍 Verifique se o backend está rodando e se a rota está configurada');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const testGetStrategies = async () => {
    setIsLoading(true);
    addLog('🔍 Testando busca de estratégias...');
    
    try {
      const result = await templateStrategyService.getStrategiesByClient(formData.client_id);
      addLog(`✅ Estratégias encontradas: ${JSON.stringify(result, null, 2)}`);
    } catch (error: any) {
      addLog(`❌ Erro ao buscar estratégias: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const testValidateConfiguration = async () => {
    setIsLoading(true);
    addLog('🔍 Testando validação de configuração...');
    
    try {
      const result = await templateStrategyService.validateClientConfiguration(formData.client_id);
      addLog(`✅ Configuração validada: ${JSON.stringify(result, null, 2)}`);
    } catch (error: any) {
      addLog(`❌ Erro ao validar configuração: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle>🔍 Debug Template Strategies</CardTitle>
          <CardDescription>
            Componente para testar e debugar a API de template strategies
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="client_id">Client ID</Label>
              <Input
                id="client_id"
                type="number"
                value={formData.client_id}
                onChange={(e) => handleInputChange('client_id', parseInt(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="template_id">Template ID</Label>
              <Input
                id="template_id"
                type="number"
                value={formData.template_id}
                onChange={(e) => handleInputChange('template_id', parseInt(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="strategy_type">Strategy Type</Label>
              <Select
                value={formData.strategy_type}
                onValueChange={(value) => handleInputChange('strategy_type', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="notification">Notification</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="tag_code">Tag Code</Label>
              <Input
                id="tag_code"
                value={formData.tag_code}
                onChange={(e) => handleInputChange('tag_code', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="days_before_due">Days Before Due</Label>
              <Input
                id="days_before_due"
                type="number"
                value={formData.days_before_due}
                onChange={(e) => handleInputChange('days_before_due', parseInt(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="days_after_due">Days After Due</Label>
              <Input
                id="days_after_due"
                type="number"
                value={formData.days_after_due}
                onChange={(e) => handleInputChange('days_after_due', parseInt(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="send_time">Send Time</Label>
              <Input
                id="send_time"
                type="time"
                value={formData.send_time}
                onChange={(e) => handleInputChange('send_time', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="max_attempts">Max Attempts</Label>
              <Input
                id="max_attempts"
                type="number"
                value={formData.max_attempts}
                onChange={(e) => handleInputChange('max_attempts', parseInt(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Input
                id="priority"
                type="number"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', parseInt(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="is_active">Active</Label>
              <Select
                value={formData.is_active ? 'true' : 'false'}
                onValueChange={(value) => handleInputChange('is_active', value === 'true')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">True</SelectItem>
                  <SelectItem value="false">False</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="business_rule">Business Rule</Label>
            <Textarea
              id="business_rule"
              value={formData.business_rule}
              onChange={(e) => handleInputChange('business_rule', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>🧪 Testes</CardTitle>
          <CardDescription>
            Execute os testes para verificar a funcionalidade da API
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={testApiConfiguration}
              disabled={isLoading}
              variant="outline"
            >
              🔍 Testar Configuração da API
            </Button>
            
            <Button
              onClick={testCreateStrategy}
              disabled={isLoading}
              variant="default"
            >
              🚀 Testar Criação
            </Button>
            
            <Button
              onClick={testGetStrategies}
              disabled={isLoading}
              variant="secondary"
            >
              📋 Testar Busca
            </Button>
            
            <Button
              onClick={testValidateConfiguration}
              disabled={isLoading}
              variant="outline"
            >
              ✅ Testar Validação
            </Button>
            
            <Button
              onClick={clearLogs}
              variant="destructive"
            >
              🗑️ Limpar Logs
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>📝 Logs de Debug</CardTitle>
          <CardDescription>
            Resultados dos testes executados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-100 p-4 rounded-md max-h-96 overflow-y-auto">
            {testResults.length === 0 ? (
              <p className="text-gray-500">Nenhum teste executado ainda...</p>
            ) : (
              <div className="space-y-2">
                {testResults.map((log, index) => (
                  <div key={index} className="text-sm font-mono">
                    {log}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TemplateStrategyDebug;
